// src/components/boards/BoardTree.tsx
import { useState } from 'react';
import { ChevronRight, Heart } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { CollectionTree } from '@/hooks/useCollections';
// Removed framer-motion for memory optimization - using CSS transitions instead

interface BoardTreeProps {
  board: CollectionTree;
  /** Is this board checked for the current ad? */
  isChecked: (boardId: string) => boolean;
  /** Toggle membership; optional `next` forces a value */
  onToggle: (boardId: string, next?: boolean) => void;
  /** Create a sub-board under board.id */
  onCreateSub?: (parentId: string, name: string) => Promise<void>;
  nestingLevel?: number;
  searchQuery?: string;
}

export function BoardTree({
  board,
  isChecked,
  onToggle,
  onCreateSub,
  nestingLevel = 0,
  searchQuery = '',
}: BoardTreeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = (board.children?.length ?? 0) > 0;

  const matchesSearch = searchQuery
    ? board.name.toLowerCase().includes(searchQuery.toLowerCase())
    : true;

  if (!matchesSearch) return null;

  const checked = isChecked(board.id);

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer",
          "hover:bg-white/10 transition-colors"
        )}
        style={{ paddingLeft: `${nestingLevel * 20}px` }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle(board.id); // toggle relative
        }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="rounded transition-all text-white/60 hover:text-white"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 transition-transform",
                isExpanded && "rotate-90"
              )}
            />
          </button>
        ) : (
          <div className="h-4 w-4" />
        )}

        <Checkbox
          id={`board-${board.id}`}
          checked={checked}
          onCheckedChange={(val) => {
            // force a specific next state
            onToggle(board.id, !!val);
          }}
          className="transition-all"
          onClick={(e) => e.stopPropagation()}
        />

        <label
          htmlFor={`board-${board.id}`}
          className="flex-1 text-sm cursor-pointer select-none flex items-center gap-2 text-white/90"
          onClick={(e) => e.stopPropagation()}
        >
          {board.is_system && board.slug === 'favorites' && (
            <Heart className="h-4 w-4 text-primary" />
          )}
          <span className='ml-2'>{board.name}</span>
        </label>
      </div>

      {hasChildren && isExpanded && (
        <div
          className={cn(
            "overflow-hidden transition-all duration-200 ease-out",
            isExpanded ? "opacity-100" : "opacity-0"
          )}
        >
          {board.children.map((child) => (
            <BoardTree
              key={child.id}
              board={child}
              isChecked={isChecked}
              onToggle={onToggle}
              onCreateSub={onCreateSub}
              nestingLevel={nestingLevel + 1}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
}
